import { action, computed, observable } from 'mobx'
import dataURLFromImage from '../utils/dataURLFromImage'
import lang from '../stores/lang'
import request from '../utils/request'
import moment from 'moment'

class Patient {
  @observable fetching = false
  @observable fetchError = false
  @observable updateScheduled = false
  @observable updateError = false
  @observable fields = null
  @observable readonly = false
  fieldsUpdate = null
  updateTimeout = null

  @computed
  get birthYear() {
    return this.fields.birthday.split("-")[0]
  }

  @computed
  get birthMonth() {
    return this.fields.birthday.split('-')[1]
  }

  @computed
  get birthDay() {
    return this.fields.birthday.split('-')[2]
  }

  @computed
  get age() {
    return moment().diff(this.fields.birthday, 'years')
  }

  @computed
  get daysInBirthMonth() {
    return moment(this.birthYear + "-" + this.birthMonth, "YYYY-MM").daysInMonth()
  }

  @computed
  get hasOpticNerves() {
    return !!(
      this.fields.leftOpticNerve ||
      this.fields.rightOpticNerve
    )
  }

  @computed
  get mostRecentLeftVFI() {
    return (
      this.fields.leftRecentVFI ||
      this.fields.leftPreviousVFI ||
      this.fields.leftBaselineVFI ||
      ''
    )
  }

  @computed
  get mostRecentRightVFI() {
    return (
      this.fields.rightRecentVFI ||
      this.fields.rightPreviousVFI ||
      this.fields.rightBaselineVFI ||
      ''
    )
  }

  @computed
  get hasVFIs() {
    return !!(
      this.mostRecentLeftVFI ||
      this.mostRecentRightVFI
    )
  }
  
  @computed
  get hasRecentVisualFields() {
    return !!(
      this.fields.leftRecentField ||
      this.fields.rightRecentField
    )
  }

  @computed
  get hasPreviousVisualFields() {
    return !!(
      this.fields.leftPreviousField ||
      this.fields.rightPreviousField
    )
  }

  @computed
  get hasBaselineVisualFields() {
    return !!(
      this.fields.leftBaselineField ||
      this.fields.rightBaselineField
    )
  }

  @computed
  get hasVisualFields() {
    return (
      this.hasRecentVisualFields ||
      this.hasPreviousVisualFields ||
      this.hasBaselineVisualFields
    )
  }

  @computed
  get hasMedications() {
    return !!this.fields.medications.length
  }

  @computed
  get hasSurgeries() {
    return !!this.fields.surgeries.length
  }

  @computed
  get ethnicityImage() {
    const suffix = this.fields.gender === 'MALE' ? 'man' : 'woman'
    let prefix

    switch (this.fields.ethnicity) {
      case 'EAST_ASIAN':
        prefix = 'asian'
        break;
      case 'BLACK_AFRICAN_AMERICAN':
        prefix = 'black'
        break;
      case 'HISPANIC_LATINO':
        prefix = 'hispanic'
        break;
      case 'SOUTH_ASIAN':
        prefix = 'indian'
        break;
      default:
        prefix = 'white'
        break;
    }

    return `/images/stories/${prefix}-${suffix}.png`
  }

  @computed
  get hasStrengths() {
    return !!this.fields.strengths.length
  }

  @computed
  get hasMotivations() {
    return !!this.fields.motivations.length
  }

  @computed
  get hasPlans() {
    return !!this.fields.plans.length
  }

  @computed
  get isCombat() {
    return !!(this.fields && this.fields.provider === 'combat')
  }

  @action
  reset = () => {
    clearTimeout(this.updateTimeout)
    this.fetching = false
    this.fetchError = false
    this.updateScheduled = false
    this.updateError = false
    this.fields = null
    this.fieldsUpdate = null
    this.readonly = false
  }

  @action
  setFetching = () => {
    this.fetching = true
    this.fetchError = false
  }

  @action
  setFetchError = () => {
    this.fetching = false
    this.fetchError = true
  }

  @action
  setFetched = fields => {
    this.fields = fields
    this.fieldsUpdate = {}
    this.fetching = false
    this.fetchError = false
    this.fields.octLeftS = this.fields.octLeftS || 'GREEN';
    this.fields.octLeftN = this.fields.octLeftN || 'GREEN';
    this.fields.octLeftI = this.fields.octLeftI || 'GREEN';
    this.fields.octLeftT = this.fields.octLeftT || 'GREEN';
    this.fields.octRightS = this.fields.octRightS || 'GREEN';
    this.fields.octRightN = this.fields.octRightN || 'GREEN';
    this.fields.octRightI = this.fields.octRightI || 'GREEN';
    this.fields.octRightT = this.fields.octRightT || 'GREEN';
  }

  @action
  scheduleUpdate = () => {
    clearTimeout(this.updateTimeout)
    this.updateTimeout = setTimeout(this.update, 300)
    this.updateScheduled = true
  }

  @action
  setUpdated = () => {
    this.updateScheduled = false
    this.updateError = false
    this.fieldsUpdate = {}
  }

  @action
  setUpdateError = () => {
    this.updateScheduled = false
    this.updateError = true
  }

  @action
  setField = event => {
    this.fields[event.target.name] = event.target.value
    this.fieldsUpdate[event.target.name] = event.target.value
    this.scheduleUpdate()
  }

  @action
  setIsChristian = event => {
    const isChristian = event.target.value
    this.fields.isChristian = isChristian
    this.fieldsUpdate.isChristian = isChristian
    if(isChristian === 'NO') {
      this.fields.whatFaith = ''
      this.fieldsUpdate.whatFaith = ''
    }
    this.scheduleUpdate()
  }

  @action
  setBirthYear = event => {
    let year = event.target.value
    let month = this.birthMonth
    let day = this.birthDay
    let maxDay = moment(year + "-" + month, "YYYY-MM").daysInMonth()
    if (maxDay < day) {
      day = maxDay
    }
    this.fields.birthday = year + "-" + month + "-" + day
    this.fieldsUpdate.birthday = this.fields.birthday
    this.scheduleUpdate()
  }

  @action
  setBirthMonth = event => {
    let year = this.birthYear
    let month = event.target.value
    let day = this.birthDay
    let maxDay = moment(year + "-" + month, "YYYY-MM").daysInMonth()
    if (maxDay < day) {
      day = maxDay
    }
    this.fields.birthday = year + "-" + month + "-" + day
    this.fieldsUpdate.birthday = this.fields.birthday
    this.scheduleUpdate()
  }

  @action
  setBirthDay = event => {
    let year = this.birthYear
    let month = this.birthMonth
    let day = event.target.value
    this.fields.birthday = year + "-" + month + "-" + day
    this.fieldsUpdate.birthday = this.fields.birthday
    this.scheduleUpdate()
  }

  @action
  setVFI = event => {
    const oldValue = this.fields[event.target.name]
    let newValue = event.target.value.replace(/\D/g, '').slice(0, 3)

    if (newValue !== '' && parseInt(newValue, 10) <= 100) {
      newValue = parseInt(newValue, 10).toString()
    } else if (newValue !== '') {
      newValue = oldValue
    }

    if (oldValue !== newValue) {
      this.fields[event.target.name] = newValue
      this.fieldsUpdate[event.target.name] = newValue
      this.scheduleUpdate()
    }
  }

  @action
  toggleSurgery = value => {
    if (this.fields.surgeries.includes(value)) {
      this.fields.surgeries = this.fields.surgeries.filter(v => v !== value)
    } else {
      this.fields.surgeries.push(value)
    }

    this.fieldsUpdate.surgeries = this.fields.surgeries
    this.scheduleUpdate()
  }

  @action
  toggleScreeningReason = value => {
    if (this.fields.screeningReasons.includes(value)) {
      this.fields.screeningReasons = this.fields.screeningReasons.filter(v => v !== value)
    } else {
      this.fields.screeningReasons.push(value)
    }

    this.fieldsUpdate.screeningReasons = this.fields.screeningReasons
    this.scheduleUpdate()
  }

  @action
  addMedication = fields => {
    this.fields.medications.unshift(fields)
    this.fieldsUpdate.medications = this.fields.medications
    this.scheduleUpdate()
  }

  @action
  setMedicationField = (index, event) => {
    if (event.target.name === 'frequency') {
      const value = event.target.value.replace(/\D/g, '').slice(0, 3)
      
      if (value !== this.fields.medications[index].frequency) {
        this.fields.medications[index].frequency = value
        this.fieldsUpdate.medications = this.fields.medications
        this.scheduleUpdate()
      }
    } else {
      this.fields.medications[index][event.target.name] = event.target.value
      this.fieldsUpdate.medications = this.fields.medications
      this.scheduleUpdate()
    }
  }

  @action
  removeMedication = index => {
    this.fields.medications.splice(index, 1)
    this.fieldsUpdate.medications = this.fields.medications
    this.scheduleUpdate()
  }

  @action
  addMonitor = (event, index) => {
    event.preventDefault()
    if(!this.fields.medications[index].monitors) {
      this.fields.medications[index].monitors = []
    }
    this.fields.medications[index].monitors.push({})
    this.fieldsUpdate.medications = this.fields.medications
    this.scheduleUpdate()
  }

  @action
  removeMonitor = (event, medicationIndex, monitorIndex) => {
    event.preventDefault()
    if(monitorIndex>=0
      && monitorIndex<this.fields.medications[medicationIndex].monitors.length) {
      this.fields.medications[medicationIndex].monitors.splice(monitorIndex, 1)
      this.fieldsUpdate.medications = this.fields.medications
      this.scheduleUpdate()
    }
  }

  @action
  setMonitorFields = (event,medicationIndex, monitorIndex) => {
    const monitor = this.fields.medications[medicationIndex].monitors[monitorIndex]
    this.fields.medications[medicationIndex].monitors[monitorIndex] = {
      ...monitor,
      [event.target.name]: event.target.value
    }
    this.fieldsUpdate.medications = this.fields.medications
    this.scheduleUpdate()
  }
  setImage = async event => {
    const data = await dataURLFromImage(event.target.files[0])
    
    this.setField({
      target: {
        name: event.target.name,
        value: data,
      },
    })
  }

  @action
  removeImage = image => {
    this.fields[image] = ''
    this.fields[`${image}Date`] = ''
    this.fieldsUpdate[image] = ''
    this.fieldsUpdate[`${image}Date`] = ''
    this.scheduleUpdate()
  }

  @action
  toggleBooleanField = name => {
    this.fields[name] = !this.fields[name]
    this.fieldsUpdate[name] = this.fields[name]
    this.scheduleUpdate()
  }

  @action
  setImportance = value => {
    if (this.fields.importance !== value) {
      this.fields.importance = value
      this.fieldsUpdate.importance = value
      this.scheduleUpdate()
    }
  }

  @action
  setConfidence = value => {
    if (this.fields.confidence !== value) {
      this.fields.confidence = value
      this.fieldsUpdate.confidence = value
      this.scheduleUpdate()
    }
  }

  @action
  setHopefulness = value => {
    if (this.fields.hopefulness !== value) {
      this.fields.hopefulness = value
      this.fieldsUpdate.hopefulness = value
      this.scheduleUpdate()
    }
  }

  @action
  toggleMotivation = value => {
    if (this.fields.motivations.includes(value)) {
      this.fields.motivations = this.fields.motivations.filter(v => v !== value)
    } else {
      this.fields.motivations.push(value)
    }

    this.fieldsUpdate.motivations = this.fields.motivations
    this.scheduleUpdate()
  }

  @action
  toggleStrength = value => {
    if (this.fields.strengths.includes(value)) {
      this.fields.strengths = this.fields.strengths.filter(v => v !== value)
    } else {
      this.fields.strengths.push(value)
    }

    this.fieldsUpdate.strengths = this.fields.strengths
    this.scheduleUpdate()
  }

  @action
  addPlan = () => {
    this.fields.plans.push({
      medications: [],
      location: 'OTHER',
      time: '',
      timePeriod: 'AM',
      routine: '',
    })

    this.fieldsUpdate.plans = this.fields.plans
    this.scheduleUpdate()
  }

  @action
  setPlanField = (index, event) => {
    this.fields.plans[index][event.target.name] = event.target.value
    this.fieldsUpdate.plans = this.fields.plans
    this.scheduleUpdate()
  }

  @action
  setPlanTimePeriod = (index, value) => {
    if (value !== this.fields.plans[index].timePeriod) {
      this.fields.plans[index].timePeriod = value
      this.fieldsUpdate.plans = this.fields.plans
      this.scheduleUpdate()
    }
  }

  @action
  togglePlanMedication = (index, value) => {
    if (this.fields.plans[index].medications.includes(value)) {
      this.fields.plans[index].medications = this.fields.plans[index].medications.filter(v => v !== value)
    } else {
      this.fields.plans[index].medications.push(value)
    }

    this.fieldsUpdate.plans = this.fields.plans
    this.scheduleUpdate()
  }

  @action
  removePlan = index => {
    this.fields.plans.splice(index, 1)
    this.fieldsUpdate.plans = this.fields.plans
    this.scheduleUpdate()
  }

  @action
  setReadonlyFields = fields => {
    this.fields = fields
    this.readonly = true
  }

  fetch = async patientID => {
    if (this.fields && patientID === this.fields._id) {
      return
    }

    this.reset()
    this.setFetching()
    let res

    if (patientID === 'demo') {
      res = {
        success: true,
        data: {
          patient: {
            _id: 'demo',
            provider: 'demo',
            name: 'Jane Doe',
            gender: 'FEMALE',
            doctor: 'Newman-Casey',
            doctorGender: 'FEMALE',
            ethnicity: 'BLACK_AFRICAN_AMERICAN',
            diagnosis: 'OPEN_ANGLE',
            leftRecentVFI: '97',
            leftPreviousVFI: '99',
            leftBaselineVFI: '99',
            rightRecentVFI: '69',
            rightPreviousVFI: '70',
            rightBaselineVFI: '96',
            surgeries: [],
            screeningReasons: ["HIGH_PRESSURE", "DAMAGE"],
            medications: [{
              prescription: 'LATANOPROST',
              eye: 'BOTH',
              frequency: '1',
            }, {
              prescription: 'TIMOLOL',
              eye: 'BOTH',
              frequency: '1',
            }],
            patientNotes: 'Is there any way I can help reverse the effects of my glaucoma?',
            counselorNotes: 'Patient is concerned that her glaucoma will get worse even if she takes her drops as prescribed.',
            usesInternet: false,
            usesSmartphone: false,
            socialSupport: 'INDEPENDENT',
            importance: 5,
            confidence: 5,
            hopefulness: 4,
            motivations: [],
            strengths: [],
            planNotes: 'I will put copies of this plan and my medication routines in my bedroom and bathroom where I keep my drops, to help me not forget to take them every morning and every night.',
            plans: [{
              medications: ['TIMOLOL'],
              location: 'BATHROOM',
              time: '8:00',
              timePeriod: 'AM',
              routine: 'One drop in each eye every morning, after brushing teeth.',
            }, {
              medications: ['LATANOPROST'],
              location: 'BEDSIDE',
              time: '9:00',
              timePeriod: 'PM',
              routine: 'One drop in each eye every night, before getting into bed.',
            }],
            leftOpticNerve: '/images/demo/demo-nerve-left.jpg',
            leftRecentField: '/images/demo/demo-field-left-recent-97.png',
            leftPreviousField: '/images/demo/demo-field-left-previous-99.png',
            leftBaselineField: '/images/demo/demo-field-left-baseline-99.png',
            rightOpticNerve: '/images/demo/demo-nerve-right.jpg',
            rightRecentField: '/images/demo/demo-field-right-recent-69.png',
            rightPreviousField: '/images/demo/demo-field-right-previous-70.png',
            rightBaselineField: '/images/demo/demo-field-right-baseline-96.png',
            leftOpticNerveDate: 'April, 2015',
            leftRecentFieldDate: 'April, 2015',
            leftPreviousFieldDate: 'March, 2013',
            leftBaselineFieldDate: 'January, 2011',
            rightOpticNerveDate: 'April, 2015',
            rightRecentFieldDate: 'April, 2015',
            rightPreviousFieldDate: 'March, 2013',
            rightBaselineFieldDate: 'January, 2011',
            octLeftS: 'GREEN',
            octLeftN: 'YELLOW',
            octLeftI: 'YELLOW',
            octLeftT: 'GREEN',
            octRightS: 'GREEN',
            octRightN: 'RED',
            octRightI: 'YELLOW',
            octRightT: 'GREEN',
          },
        },
      }
    } else {
      res = await request('/api/patient/fetch', { patientID })
    }

    if (res.success) {
      this.setFetched(res.data.patient)
    } else {
      this.setFetchError()
    }
  }

  update = async () => {
    if (this.fields._id === 'demo') {
      this.setUpdated()
      return
    } else {
      const res = await request('/api/patient/update', {
        ...this.fieldsUpdate,
        patientID: this.fields._id,
      })

      if (res.success) {
        this.setUpdated()
      } else {
        this.setUpdateError()
      }
    }
  }

  remove = async () => {
    if (window.confirm(lang.translate('prose.deletePatientConfirm'))) {
      await request('/api/patient/remove', { patientID: this.fields._id })
      window.location = this.fields.demo ? '/demo' : '/patients'
    }
  }
}

export default new Patient()



// WEBPACK FOOTER //
// ./src/stores/patient.js